import { ComponentType } from "@angular/cdk/portal";
import { inject, Injectable, Injector, NgZone } from "@angular/core";
import { MatDialog, MatDialogConfig, MatDialogRef, MatDialogState } from "@angular/material/dialog";
import { filter, fromEvent, merge, takeUntil } from "rxjs";
import { RIGHT_PANEL_CONFIG } from "@bitwarden/web-vault/app/models/constants/global.constants";
import { ConfirmationEnum } from "@bitwarden/web-vault/app/models/enum/confirmation.enum";
import { ConfirmationDialogService } from "@bitwarden/web-vault/app/services/confirmation/confirmation.service";
import { keepDialogEscapeDisabled } from "@bitwarden/web-vault/app/services/dialog-escape.service";
import { decideEscape, isDismissEscape } from "@bitwarden/web-vault/app/shared/utils/escape-claim";

const BUSY_SELECTOR = "[aria-busy='true']";

export interface RightPanelOptions {
  /**
   * While true, click-away and Escape are ignored (e.g. a save request is in flight). Always
   * applies on top of the panel's own busy controls: a button marked `aria-busy="true"` inside
   * the panel (a submit button that is saving) also counts as busy.
   */
  isBusy?: () => boolean;
  /**
   * Whether the panel holds input the user would lose. Replaces the default check (the user typed
   * into the panel, or a control inside it is `ng-dirty`) for panels whose state lives outside
   * Angular forms, e.g. a custom picker.
   */
  hasUnsavedInput?: () => boolean;
}

/**
 * Opens slide-out panels that close on click-away and Escape.
 *
 * Material's `disableClose` turns both off and closes nothing itself, so the panel opts out of
 * Material's own dismissal and re-implements it here with two guards Material can't offer:
 * ignore the dismissal while the caller is busy, and ask before discarding edited form input.
 *
 * Escape is taken over from DialogEscapeService (which would otherwise press the X of every
 * `disableClose` dialog, with no unsaved-input prompt) and keeps its rules: no key repeat, no IME
 * composition, and not when something inside the panel (a dropdown, a popover) used the press.
 */
@Injectable({ providedIn: "root" })
export class RightPanelService {
  private dialog = inject(MatDialog);
  // Resolved lazily so panels opened in contexts without confirmation providers still work
  // until a dirty form actually needs the prompt.
  private injector = inject(Injector);
  private zone = inject(NgZone);

  open<T, D = unknown, R = unknown>(
    component: ComponentType<T>,
    config: Omit<MatDialogConfig<D>, "disableClose"> = {},
    options: RightPanelOptions = {},
  ): MatDialogRef<T, R> {
    const ref = this.dialog.open<T, D, R>(component, {
      ...RIGHT_PANEL_CONFIG,
      ...config,
      disableClose: true,
    });
    this.guardDismissal(ref, options);
    return ref;
  }

  /** Same guards for a panel that has to be opened by the caller (e.g. with its own config). */
  guardDismissal(ref: MatDialogRef<unknown, unknown>, options: RightPanelOptions = {}): void {
    // Escape is handled here, with the unsaved-input guard, not by pressing the X.
    keepDialogEscapeDisabled(ref);

    const edited = this.watchUserInput(ref);
    const hasUnsavedInput = () =>
      options.hasUnsavedInput?.() ?? (edited() || this.hasDirtyControl(ref));

    let confirming = false;
    const isBusy = () => !!options.isBusy?.() || this.hasBusyControl(ref);
    const tryClose = async () => {
      if (confirming || isBusy()) {
        return;
      }
      if (hasUnsavedInput()) {
        confirming = true;
        try {
          const discard = await this.injector
            .get(ConfirmationDialogService)
            .confirmFor(ConfirmationEnum.discardingUnsavedChanges);
          // The panel may have started saving, or closed itself, while the prompt was open.
          if (!discard || isBusy() || ref.getState() !== MatDialogState.OPEN) {
            return;
          }
        } finally {
          confirming = false;
        }
      }
      ref.close();
    };

    ref
      .backdropClick()
      .pipe(takeUntil(ref.afterClosed()))
      .subscribe(() => void tryClose());

    ref
      .keydownEvents()
      .pipe(filter(isDismissEscape), takeUntil(ref.afterClosed()))
      .subscribe((event) => {
        // Waits for the press to finish propagating, so an Escape that collapsed a dropdown or was
        // claimed inside the panel doesn't also close it.
        const pane = document.getElementById(ref.id)?.closest<HTMLElement>(".cdk-overlay-pane");
        decideEscape(pane, event, (claimedInside) => {
          if (!claimedInside) {
            void tryClose();
          }
        });
      });
  }

  /** A control inside the panel that reports a request in flight (see gloss-button). */
  private hasBusyControl(ref: MatDialogRef<unknown, unknown>): boolean {
    return !!document.getElementById(ref.id)?.querySelector(BUSY_SELECTOR);
  }

  /**
   * Whether the user has typed into or changed a native control inside the panel. Angular's
   * `ng-dirty` misses controls that never touch an Angular form (a custom picker that sets a
   * signal), while a form marked dirty in code says nothing about the user, so both are used.
   */
  private watchUserInput(ref: MatDialogRef<unknown, unknown>): () => boolean {
    let edited = false;
    // Outside Angular: a bookkeeping listener shouldn't trigger change detection on every key.
    this.zone.runOutsideAngular(() => {
      merge(
        fromEvent<Event>(document, "input", { capture: true }),
        fromEvent<Event>(document, "change", { capture: true }),
      )
        .pipe(
          filter(
            (event) =>
              event.isTrusted &&
              event.target instanceof Node &&
              !!document.getElementById(ref.id)?.contains(event.target),
          ),
          takeUntil(ref.afterClosed()),
        )
        .subscribe(() => (edited = true));
    });
    return () => edited;
  }

  /** Angular marks a control `ng-dirty` once the user (or code) edits it. */
  private hasDirtyControl(ref: MatDialogRef<unknown, unknown>): boolean {
    return !!document.getElementById(ref.id)?.querySelector(".ng-dirty");
  }
}
